import { getMedicineTransactionQuantity } from "./medicineInventorySummary";

export const EMPTY_PATH_INVENTORY_SUMMARY = {
  totalItems: 0,
  totalQuantity: 0,
  inTransactions: 0,
  inQuantity: 0,
  outTransactions: 0,
  outQuantity: 0,
  allTransactions: 0,
  byDepartment: [],
};

const normalizeName = (value) => (value || "").toString().trim().toLowerCase();

const getTransactionItemName = (transaction) =>
  transaction?.inventoryName ||
  transaction?.itemName ||
  transaction?.medicineName ||
  transaction?.name ||
  "";

const isInboundTransaction = (transactionType) => {
  const type = String(transactionType || "").toUpperCase();
  return type === "IN" || type === "RETURN";
};

const isOutboundTransaction = (transactionType) => {
  return String(transactionType || "").toUpperCase() === "OUT";
};

export const isCompleteLabTestStatus = (status) => {
  const normalized = String(status || "").toUpperCase();
  return normalized === "COMPLETE" || normalized === "COMPLETED";
};

/** Inventory is consumed at sample collection — COLLECTED and COMPLETE both imply OUT. */
export const isPathInventoryConsumedStatus = (status) => {
  const normalized = String(status || "").toUpperCase();
  return (
    normalized === "COLLECTED" ||
    normalized === "COMPLETE" ||
    normalized === "COMPLETED"
  );
};

const ensureCategoryEntry = (categoryMap, category) => {
  const key = category || "Uncategorized";
  if (!categoryMap.has(key)) {
    categoryMap.set(key, {
      department: key,
      items: 0,
      totalQuantity: 0,
      inQuantity: 0,
      outQuantity: 0,
      transactions: 0,
    });
  }
  return categoryMap.get(key);
};

export function parseLabTestBomEntries(labTest, bomsById = new Map()) {
  if (!labTest) return [];

  const entries = [];

  if (Array.isArray(labTest.bomEntries) && labTest.bomEntries.length > 0) {
    labTest.bomEntries.forEach((entry) => {
      const bomId = Number(entry?.bom_id ?? entry?.bomId ?? entry?.id);
      if (!bomId || Number.isNaN(bomId)) return;

      const bom = bomsById.get(bomId);
      let requiredQuantity = Number(
        entry?.requiredQuantity ?? entry?.required_quantity ?? entry?.requiredquantity
      );
      if (!requiredQuantity || Number.isNaN(requiredQuantity)) {
        requiredQuantity = Number(bom?.requiredQuantity) || 0;
      }

      entries.push({
        bomId,
        itemName: bom?.name || "",
        requiredQuantity,
      });
    });
    return entries.filter((entry) => entry.itemName && entry.requiredQuantity > 0);
  }

  if (labTest.bomId != null && String(labTest.bomId).trim() !== "") {
    String(labTest.bomId)
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
      .forEach((idValue) => {
        const bomId = Number(idValue);
        if (!bomId || Number.isNaN(bomId)) return;
        const bom = bomsById.get(bomId);
        if (!bom?.name) return;
        entries.push({
          bomId,
          itemName: bom.name,
          requiredQuantity: Number(bom.requiredQuantity) || 0,
        });
      });
  }

  return entries.filter((entry) => entry.itemName && entry.requiredQuantity > 0);
}

export function buildLabTestLookupMaps(labTests = []) {
  const byId = new Map();
  const byName = new Map();

  (Array.isArray(labTests) ? labTests : []).forEach((labTest) => {
    const id = Number(labTest?.id);
    if (id && !Number.isNaN(id)) {
      byId.set(id, labTest);
    }
    const name = normalizeName(labTest?.name || labTest?.testName);
    if (name && !byName.has(name)) {
      byName.set(name, labTest);
    }
  });

  return { byId, byName };
}

export function resolveLabTestForUsage({
  labTestId,
  testName,
  labTestsById = new Map(),
  labTestsByName = new Map(),
}) {
  const numericId = Number(labTestId);
  if (numericId && !Number.isNaN(numericId) && labTestsById.has(numericId)) {
    return labTestsById.get(numericId);
  }

  const normalizedName = normalizeName(testName);
  if (normalizedName && labTestsByName.has(normalizedName)) {
    return labTestsByName.get(normalizedName);
  }

  return null;
}

export function buildPathInventoryTransactionsFromRecords(inventoryRows = []) {
  const rows = Array.isArray(inventoryRows) ? inventoryRows : [];
  const transactions = [];

  rows.forEach((group) => {
    (group?.records || []).forEach((record) => {
      const qty = Number(record?.quantity) || 0;
      if (qty <= 0) return;
      transactions.push({
        transactionType: "IN",
        itemName: group?.name || record?.name,
        deductedQuantity: qty,
      });
    });
  });

  return transactions;
}

export function buildPathInventoryOutFromLabTestUsages({
  opdReports = [],
  ipdLabTests = [],
  labTests = [],
  boms = [],
}) {
  const bomsById = new Map(
    (Array.isArray(boms) ? boms : []).map((bom) => [Number(bom.id), bom])
  );
  const { byId: labTestsById, byName: labTestsByName } = buildLabTestLookupMaps(labTests);
  const transactions = [];
  const seenOpd = new Set();
  const seenIpd = new Set();

  (Array.isArray(opdReports) ? opdReports : []).forEach((report) => {
    if (!isPathInventoryConsumedStatus(report?.resultStatus)) return;

    const reportKey = report?.reportId ?? report?.id;
    if (reportKey != null) {
      if (seenOpd.has(reportKey)) return;
      seenOpd.add(reportKey);
    }

    const labTest = resolveLabTestForUsage({
      labTestId: report?.labTestId ?? report?.testId,
      testName: report?.testName,
      labTestsById,
      labTestsByName,
    });
    if (!labTest) return;

    parseLabTestBomEntries(labTest, bomsById).forEach((entry) => {
      transactions.push({
        transactionType: "OUT",
        itemName: entry.itemName,
        deductedQuantity: entry.requiredQuantity,
        labTestId: labTest.id,
        labTestName: report?.testName || labTest?.name || labTest?.testName,
        sourceType: "OPD",
      });
    });
  });

  (Array.isArray(ipdLabTests) ? ipdLabTests : []).forEach((test) => {
    const status = test?.testStatus || test?.resultStatus;
    if (!isPathInventoryConsumedStatus(status)) return;

    const testKey = test?.id;
    if (testKey != null) {
      if (seenIpd.has(testKey)) return;
      seenIpd.add(testKey);
    }

    const labTest = resolveLabTestForUsage({
      labTestId: test?.testId ?? test?.labTestId,
      testName: test?.testName || test?.resolvedTestName,
      labTestsById,
      labTestsByName,
    });
    if (!labTest) return;

    parseLabTestBomEntries(labTest, bomsById).forEach((entry) => {
      transactions.push({
        transactionType: "OUT",
        itemName: entry.itemName,
        deductedQuantity: entry.requiredQuantity,
        labTestId: labTest.id,
        labTestName: test?.testName || test?.resolvedTestName || labTest?.name,
        sourceType: "IPD",
      });
    });
  });

  return transactions;
}

export function buildPathInventoryExportSummary({
  inventoryRows = [],
  transactions = [],
}) {
  const rows = Array.isArray(inventoryRows) ? inventoryRows : [];
  const txs = Array.isArray(transactions) ? transactions : [];

  if (rows.length === 0 && txs.length === 0) {
    return { ...EMPTY_PATH_INVENTORY_SUMMARY };
  }

  const itemNameToCategory = new Map();
  const categoryMap = new Map();

  rows.forEach((row) => {
    const category = row?.bomName || row?.name || "Uncategorized";
    const nameKey = normalizeName(row?.name);
    if (nameKey) {
      itemNameToCategory.set(nameKey, category);
    }
    const entry = ensureCategoryEntry(categoryMap, category);
    entry.items += 1;
    entry.totalQuantity += Number(row?.totalQuantity) || 0;
  });

  const itemNames = new Set(
    rows.map((row) => normalizeName(row?.name)).filter(Boolean)
  );
  const relevantTransactions =
    itemNames.size > 0
      ? txs.filter((tx) => itemNames.has(normalizeName(getTransactionItemName(tx))))
      : txs;

  let inTransactions = 0;
  let inQuantity = 0;
  let outTransactions = 0;
  let outQuantity = 0;

  relevantTransactions.forEach((tx) => {
    const qty = getMedicineTransactionQuantity(tx);
    const category =
      itemNameToCategory.get(normalizeName(getTransactionItemName(tx))) ||
      getTransactionItemName(tx) ||
      "Uncategorized";
    const entry = ensureCategoryEntry(categoryMap, category);
    entry.transactions += 1;

    if (isInboundTransaction(tx?.transactionType)) {
      inTransactions += 1;
      inQuantity += qty;
      entry.inQuantity += qty;
      return;
    }

    if (isOutboundTransaction(tx?.transactionType)) {
      outTransactions += 1;
      outQuantity += qty;
      entry.outQuantity += qty;
    }
  });

  return {
    totalItems: rows.length,
    totalQuantity: rows.reduce(
      (sum, row) => sum + (Number(row?.totalQuantity) || 0),
      0
    ),
    inTransactions,
    inQuantity,
    outTransactions,
    outQuantity,
    allTransactions: relevantTransactions.length,
    byDepartment: Array.from(categoryMap.values()).sort((a, b) =>
      String(a.department).localeCompare(String(b.department))
    ),
  };
}
